/** Job Work Orders — material out for an operation, and back again.
 *
 * The document that removes FKMT's factory-stock workaround. Material leaves
 * from wherever it is — including a project location — and returns there,
 * **without the project link ever breaking**.
 *
 * A line names a RANGE of operations, because FKMT clubs them: "stress
 * relieving two and sandblasting and primer — one invoice, one purchase
 * order", and "when I club the operation, material leaves once and comes back
 * once". So the form asks *from* which operation and *to* which, and the
 * stages follow.
 *
 * On receipt, a shortfall is never absorbed quietly. Ten went out, nine came
 * back — the tenth is accounted for by somebody, on purpose.
 *
 * JOB_WORK.md.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { SearchSelect } from '@/components/SearchSelect'
import { useObjectRights } from '@/lib/rights'

interface Order {
  jobwork_id: string; doc_no: string | null; doc_date: string
  status: string; supplier_name: string; location_id: string
  project_id: string | null; lines: number; version: number
}
interface Line {
  jobwork_line_id: string; line_no: number
  item_id: string; item_name: string
  from_op_seq: number; to_op_seq: number
  from_stage: string; to_stage: string
  qty: string; received_qty: string; shortfall_qty: string
  shortfall_disposition: string | null
}
interface RouteOp {
  op_seq: number; process_name: string
  input_stage: string; result_stage: string; is_job_work: boolean
}
interface Named { code: string; name: string }

/** STRESS_RELIEVED_2 → "Stress relieved - 2", as the PM app writes a later pass. */
function stageLabel(code: string): string {
  if (!code) return ''
  const m = /^(.*?)_(\d+)$/.exec(code)
  const base = (m ? m[1] : code).replace(/_/g, ' ').toLowerCase()
  const pretty = base.charAt(0).toUpperCase() + base.slice(1)
  return m ? `${pretty} - ${m[2]}` : pretty
}

const DISPOSITIONS = [
  { value: '', label: '— choose —' },
  { value: 'RecoverFromSupplier', label: 'Recoverable from the subcontractor' },
  { value: 'AbsorbToProject', label: 'Absorbed by the project' },
  { value: 'WriteOff', label: 'Written off as loss' },
]

export function JobWorkScreen() {
  const [orders, setOrders] = useState<Order[]>([])
  const [open, setOpen] = useState<{ header: Order & Record<string, unknown>
                                     lines: Line[] } | null>(null)
  const [creating, setCreating] = useState(false)
  // jobwork.order.add / issue / receive — what jobwork/index.py checks.
  // For deciding what to SHOW; the server decides. RIGHTS_MODEL.md 6.1.
  const rights = useObjectRights('jobwork', 'order')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [subcontractors, setSubs] = useState<Named[]>([])
  const [locations, setLocations] = useState<Named[]>([])
  const [items, setItems] = useState<Named[]>([])
  const [ops, setOps] = useState<RouteOp[]>([])

  const [head, setHead] = useState({
    supplier_code: '', location_id: '',
    doc_date: new Date().toISOString().slice(0, 10),
    expected_date: '', project_id: '', remarks: '',
  })
  const [line, setLine] = useState({ item_id: '', from_op_seq: '',
                                     to_op_seq: '', qty: '' })
  const [receipts, setReceipts] = useState<Record<number, {
    qty: string; cost: string; shortfall_qty: string
    shortfall_disposition: string }>>({})

  const load = useCallback(async () => {
    setBusy(true)
    try {
      const r = await api.get<{ rows: Order[] }>('/jobwork/')
      setOrders(r.rows)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Orders could not be loaded.')
    } finally { setBusy(false) }
  }, [])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    void (async () => {
      const pull = async (path: string, q: Record<string, string>,
                          code: string, name: string) => {
        try {
          const r = await api.get<{ rows: Record<string, unknown>[] }>(path, q)
          return r.rows.map(x => ({ code: String(x[code]), name: String(x[name]) }))
        } catch { return [] }
      }
      // Only suppliers who actually do job work, and only items with a route.
      setSubs(await pull('/masters/suppliers/', { is_subcontractor: 'true' },
                         'supplier_code', 'name'))
      setLocations(await pull('/masters/locations/', {}, 'location_id', 'name'))
      setItems(await pull('/masters/items/', { needs_route: 'true' },
                          'item_id', 'item_name'))
    })()
  }, [])

  // The operations available on the chosen item, so the range is picked and
  // never typed.
  useEffect(() => {
    if (!line.item_id) { setOps([]); return }
    void (async () => {
      try {
        const r = await api.get<{ rows: RouteOp[] }>(`/jobwork/route/${line.item_id}`)
        setOps(r.rows)
      } catch { setOps([]) }
    })()
  }, [line.item_id])

  const jobOps = ops.filter(o => o.is_job_work)
  const chosenOp = jobOps.find(o => String(o.op_seq) === line.from_op_seq)

  // What is on the shelf, asked as soon as the item, location and operation
  // are chosen — not held back until Save. A storekeeper filling in a
  // quantity should be told what there is while they are typing it.
  const [onHand, setOnHand] = useState<{ qty: number; stage: string } | null>(null)
  useEffect(() => {
    if (!line.item_id || !head.location_id || !chosenOp) { setOnHand(null); return }
    let live = true
    void (async () => {
      try {
        const r = await api.get<{ qty: string; stage: string }>('/jobwork/stock', {
          item_id: line.item_id, location_id: head.location_id,
          stage: chosenOp.input_stage,
        })
        if (live) setOnHand({ qty: Number(r.qty), stage: r.stage })
      } catch { if (live) setOnHand(null) }
    })()
    return () => { live = false }
  }, [line.item_id, head.location_id, chosenOp])

  const short = onHand !== null && Number(line.qty || 0) > onHand.qty

  async function openOrder(id: string) {
    setBusy(true); setError(null)
    try {
      setOpen(await api.get(`/jobwork/${id}`))
      setReceipts({})
      setCreating(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'It could not be opened.')
    } finally { setBusy(false) }
  }

  async function create(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const r = await api.create<{ jobwork_id: string }>('/jobwork/', {
        ...head,
        expected_date: head.expected_date || null,
        project_id: head.project_id || null,
        lines: [{
          item_id: line.item_id,
          from_op_seq: Number(line.from_op_seq),
          to_op_seq: Number(line.to_op_seq || line.from_op_seq),
          qty: Number(line.qty),
        }],
      })
      setCreating(false)
      setLine({ item_id: '', from_op_seq: '', to_op_seq: '', qty: '' })
      await load()
      await openOrder(r.jobwork_id)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'It could not be saved.')
    } finally { setBusy(false) }
  }

  async function act(id: string, action: string, body: unknown = {}) {
    setBusy(true); setError(null)
    try {
      await api.create(`/jobwork/${id}/${action}`, body)
      await load()
      await openOrder(id)
    } catch (err) {
      setError(err instanceof ApiError ? err.message
                                       : 'The action could not be completed.')
    } finally { setBusy(false) }
  }

  function receiveAll() {
    if (!open) return
    const lines = open.lines
      .filter(l => Number(l.qty) - Number(l.received_qty) > 0)
      .map(l => {
        const r = receipts[l.line_no] ?? { qty: '', cost: '', shortfall_qty: '',
                                           shortfall_disposition: '' }
        return {
          line_no: l.line_no,
          qty: Number(r.qty || 0),
          cost: Number(r.cost || 0),
          shortfall_qty: Number(r.shortfall_qty || 0),
          shortfall_disposition: r.shortfall_disposition || null,
        }
      })
      .filter(r => r.qty > 0)
    if (!lines.length) { setError('Enter what came back first.'); return }
    void act(open.header.jobwork_id, 'receive', { lines })
  }

  return (
    <>
      <h1 className="page-title">Job Work Orders</h1>
      <p className="page-sub">
        Material out for a third-party operation, and back at its next stage —
        without leaving the project it belongs to.
      </p>

      {error && <div className="form-errors" role="alert">{error}</div>}

      {/* ── new order ──────────────────────────────────────────────────── */}
      {creating && (
        <form className="card" onSubmit={create} style={{ marginBottom: 16 }}>
          <div className="card-head">New Job Work Order</div>
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="sup">Subcontractor *</label>
              <SearchSelect
                id="sup"
                value={head.supplier_code}
                onChange={v => setHead({ ...head, supplier_code: v })}
                options={subcontractors.map(s => ({
                  value: s.code, label: s.name }))}
              />
              <div className="hint">Only suppliers marked “Does Job Work”</div>
            </div>
            <div className="form-field">
              <label htmlFor="loc">From / Back To *</label>
              <SearchSelect
                id="loc"
                value={head.location_id}
                onChange={v => setHead({ ...head, location_id: v })}
                options={locations.map(l => ({
                  value: l.code, label: `${l.code} — ${l.name}` }))}
              />
              <div className="hint">A project location is as valid as a store</div>
            </div>
            <div className="form-field">
              <label htmlFor="dt">Date *</label>
              <input id="dt" type="date" required value={head.doc_date}
                     onChange={e => setHead({ ...head, doc_date: e.target.value })} />
            </div>
            <div className="form-field">
              <label htmlFor="exp">Expected Back</label>
              <input id="exp" type="date" value={head.expected_date}
                     onChange={e => setHead({ ...head, expected_date: e.target.value })} />
            </div>
            <div className="form-field span-2">
              <label htmlFor="rem">Remarks</label>
              <textarea id="rem" value={head.remarks}
                        onChange={e => setHead({ ...head, remarks: e.target.value })} />
            </div>
          </div>

          <div className="card-head">What is going out</div>
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="it">Item *</label>
              <SearchSelect
                id="it"
                value={line.item_id}
                onChange={v => setLine({ ...line, item_id: v,
                                         from_op_seq: '', to_op_seq: '' })}
                options={items.map(i => ({
                  value: i.code, label: `${i.code} — ${i.name}` }))}
              />
            </div>
            <div className="form-field">
              <label htmlFor="qt">Quantity *</label>
              <input id="qt" type="number" step="0.001" required value={line.qty}
                     className={short ? 'input-warn' : undefined}
                     onChange={e => setLine({ ...line, qty: e.target.value })} />
              {onHand === null ? (
                <div className="hint">
                  Choose the item, the location and the operation to see what
                  is on hand
                </div>
              ) : short ? (
                <div className="hint hint-warn">
                  Only {onHand.qty} at {head.location_id}, stage{' '}
                  {stageLabel(onHand.stage)} — this will be refused
                </div>
              ) : (
                <div className="hint">
                  {onHand.qty} on hand at {head.location_id}, stage{' '}
                  {stageLabel(onHand.stage)}
                </div>
              )}
            </div>
            <div className="form-field">
              <label htmlFor="fo">From Operation *</label>
              <select id="fo" required value={line.from_op_seq}
                      disabled={!line.item_id || jobOps.length === 0}
                      onChange={e => setLine({ ...line, from_op_seq: e.target.value })}>
                <option value="">—</option>
                {jobOps.map(o => (
                  <option key={o.op_seq} value={o.op_seq}>
                    {o.op_seq} · {o.process_name} (from {stageLabel(o.input_stage)})
                  </option>
                ))}
              </select>
              {/* An empty dropdown explains nothing. Say which of the two
                  reasons it is. */}
              {line.item_id && jobOps.length === 0 && (
                <div className="hint hint-warn">
                  {ops.length === 0
                    ? 'This item has no route yet — add its operations under '
                      + 'Production → Item Routes.'
                    : 'This item has a route, but no operation on it is marked '
                      + '“Sent Outside”, so there is nothing to send.'}
                </div>
              )}
            </div>
            <div className="form-field">
              <label htmlFor="to">To Operation</label>
              <select id="to" value={line.to_op_seq}
                      onChange={e => setLine({ ...line, to_op_seq: e.target.value })}>
                <option value="">same — a single operation</option>
                {jobOps.filter(o => o.op_seq >= Number(line.from_op_seq || 0))
                       .map(o => (
                  <option key={o.op_seq} value={o.op_seq}>
                    {o.op_seq} · {o.process_name} (back at {stageLabel(o.result_stage)})
                  </option>
                ))}
              </select>
              <div className="hint">
                Club several onto one order — one trip, one cost, one invoice
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" disabled={busy || short}>
              {busy ? 'Saving…' : 'Save as Draft'}
            </button>
            <button type="button" className="btn btn-secondary"
                    onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* ── one order ──────────────────────────────────────────────────── */}
      {open && !creating && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-head detail-head">
            <div>
              <div className="detail-kind">Job Work Order</div>
              <h2 className="detail-title">
                {open.header.doc_no ?? 'Draft'} — {open.header.supplier_name}
              </h2>
              <div className="detail-code">
                {String(open.header.doc_date).slice(0, 10)} ·
                from {open.header.location_id} ·
                <span className={`badge badge-${open.header.status.toLowerCase()}`}
                      style={{ marginLeft: 6 }}>{open.header.status}</span>
              </div>
            </div>
            <div className="detail-actions">
              {open.header.status === 'Draft' && rights.can('issue') && (
                <button className="btn btn-primary" disabled={busy}
                        onClick={() => void act(open.header.jobwork_id, 'issue')}>
                  Issue — send it out
                </button>
              )}
              <button className="btn btn-secondary"
                      onClick={() => setOpen(null)}>Close</button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>Item</th>
                  <th>Operations</th>
                  <th>From → To</th>
                  <th className="num" style={{ width: 90 }}>Sent</th>
                  <th className="num" style={{ width: 90 }}>Back</th>
                  {open.header.status !== 'Draft' && (
                    <th style={{ width: 320 }}>Receive</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {open.lines.map(l => {
                  const outstanding = Number(l.qty) - Number(l.received_qty)
                  const r = receipts[l.line_no] ?? { qty: '', cost: '',
                    shortfall_qty: '', shortfall_disposition: '' }
                  return (
                    <tr key={l.jobwork_line_id}>
                      <td className="num">{l.line_no}</td>
                      <td>{l.item_id}<br /><span className="hint">{l.item_name}</span></td>
                      <td>{l.from_op_seq === l.to_op_seq
                            ? `op ${l.from_op_seq}`
                            : `ops ${l.from_op_seq}–${l.to_op_seq}`}</td>
                      <td>{stageLabel(l.from_stage)} → <strong>{stageLabel(l.to_stage)}</strong></td>
                      <td className="num">{l.qty}</td>
                      <td className="num">{l.received_qty}</td>
                      {open.header.status !== 'Draft' && (
                        <td>
                          {outstanding <= 0 ? <span className="hint">complete</span>
                            // Without `receive` the line reads as a figure,
                            // not boxes with no button to post them.
                            : !rights.can('receive')
                              ? <span className="hint">{outstanding} outstanding</span> : (
                            <div className="receive-row">
                              <input type="number" step="0.001" placeholder={`qty (${outstanding} out)`}
                                     value={r.qty}
                                     onChange={e => setReceipts({ ...receipts,
                                       [l.line_no]: { ...r, qty: e.target.value } })} />
                              <input type="number" step="0.01" placeholder="operation cost ₹"
                                     value={r.cost}
                                     onChange={e => setReceipts({ ...receipts,
                                       [l.line_no]: { ...r, cost: e.target.value } })} />
                              <input type="number" step="0.001" placeholder="short"
                                     value={r.shortfall_qty}
                                     onChange={e => setReceipts({ ...receipts,
                                       [l.line_no]: { ...r, shortfall_qty: e.target.value } })} />
                              {Number(r.shortfall_qty || 0) > 0 && (
                                <select value={r.shortfall_disposition}
                                        onChange={e => setReceipts({ ...receipts,
                                          [l.line_no]: { ...r, shortfall_disposition: e.target.value } })}>
                                  {DISPOSITIONS.map(d => (
                                    <option key={d.value} value={d.value}>{d.label}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {(open.header.status === 'Issued' ||
            open.header.status === 'PartlyReceived') && rights.can('receive') && (
            <div className="form-actions">
              <button className="btn btn-primary" disabled={busy}
                      onClick={receiveAll}>
                {busy ? 'Posting…' : 'Receive'}
              </button>
              <span className="hint" style={{ alignSelf: 'center' }}>
                The cost is added to the material, at its new stage. Anything
                that did not come back must say where it went.
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── the list ───────────────────────────────────────────────────── */}
      <div className="card">
        <div className="toolbar">
          <div className="grow" />
          {rights.can('add') && (
            <button className="btn btn-primary"
                    onClick={() => { setCreating(true); setOpen(null) }}>
              New Job Work Order
            </button>
          )}
        </div>
        {orders.length === 0 ? (
          <div className="empty" style={{ padding: 24 }}>
            <p>No job work orders yet.</p>
            <p>An item needs a route with an operation marked
               <strong> Sent Outside</strong>, and a supplier marked
               <strong> Does Job Work</strong>.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Number</th>
                  <th style={{ width: 110 }}>Date</th>
                  <th>Subcontractor</th>
                  <th style={{ width: 110 }}>From</th>
                  <th className="num" style={{ width: 70 }}>Lines</th>
                  <th style={{ width: 130 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.jobwork_id} className="row-openable"
                      title="Double-click to view"
                      onDoubleClick={() => void openOrder(o.jobwork_id)}>
                    <td>{o.doc_no ?? <span className="hint">Draft</span>}</td>
                    <td>{String(o.doc_date).slice(8, 10)}-
                        {String(o.doc_date).slice(5, 7)}-
                        {String(o.doc_date).slice(0, 4)}</td>
                    <td>{o.supplier_name}</td>
                    <td>{o.location_id}</td>
                    <td className="num">{o.lines}</td>
                    <td>
                      <span className={`badge badge-${o.status.toLowerCase()}`}>
                        {o.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
